import React, { Component } from "react";
import { connect } from "react-redux";
import QHeader from "./QSheetHeader";
import QTotals from "./QSheetTotals";
import QBody from "./QSheetBody";
import qSheetActions from "../../../_actions/qSheetActions";
import payEstimateActions from "../../../_actions/payEstimateActions";
import mainTabActions from "../../../_actions/mainTabActions";
import { DOCTYPE } from "../../../_constants/docTypeConstants";

const STATUS_BADGE = {
  Draft:      { className: "bg-dark",    label: "Draft" },
  Calculated: { className: "bg-primary", label: "Calculated" },
  Checked:    { className: "bg-success", label: "Checked" },
};

class QSheet extends Component {
  state = { liveData: null, refreshing: false, cem6004Page: "", cem6004Line: "", cem6004Date: "", isSegregated: false };

  bodyRef = React.createRef();

  handleDataChange = (liveData) => this.setState({ liveData });

  // Uploads a single file; returns the promise so QSheetBody can sequence
  // multiple uploads and only refetch once the batch is complete.
  handleUploadAttachment = (file) => {
    const { tab, dispatch } = this.props;
    return dispatch(qSheetActions.uploadAttachment(tab.docId, file, tab.tabId));
  };

  // Called by QSheetBody after all files in a batch have been processed.
  handleUploadsDone = () => {
    const { tab, dispatch } = this.props;
    dispatch(qSheetActions.fetchQSheet(tab.docId, tab.tabId));
  };

  handleDeleteAttachment = (attachmentId) => {
    const { tab, dispatch } = this.props;
    dispatch(qSheetActions.deleteAttachment(tab.docId, attachmentId, tab.tabId))
      .then(() => dispatch(qSheetActions.fetchQSheet(tab.docId, tab.tabId)));
  };

  handlePrint = () => {
    window.open(`/qsheet/${this.props.tab.docId}/print`, "_blank");
  };

  handleSaveCem6004Reference = () => {
    const { tab, dispatch } = this.props;
    const { cem6004Page, cem6004Line, cem6004Date } = this.state;
    if (!cem6004Page || !cem6004Line || !cem6004Date) return;
    const dto = { pageNumber: cem6004Page, lineNumber: cem6004Line, postingDate: cem6004Date };
    dispatch(qSheetActions.saveCem6004Reference(tab.docId, dto, tab.tabId))
      .then(() => dispatch(qSheetActions.fetchQSheet(tab.docId, tab.tabId)));
  };

  handleRefreshQuantities = () => {
    const { tab, dispatch } = this.props;
    this.setState({ refreshing: true });
    dispatch(qSheetActions.refreshQuantities(tab.docId, tab.tabId))
      .then(() => dispatch(qSheetActions.fetchQSheet(tab.docId, tab.tabId)))
      .then(() => {
        this.bodyRef.current?.refreshFromServer();
        this.refreshParentEstimateDetail();
      })
      .finally(() => this.setState({ refreshing: false }));
  };

  handleSave = () => {
    const { tab, dispatch } = this.props;
    const saveData = this.bodyRef.current?.getSaveData();
    if (!saveData) return Promise.resolve();
    const payload = { ...saveData, isSegregated: this.state.isSegregated };
    return dispatch(qSheetActions.saveQSheet(tab.docId, payload, tab.tabId))
      .then(() => dispatch(qSheetActions.fetchQSheet(tab.docId, tab.tabId)))
      .then(() => {
        this.bodyRef.current?.refreshFromServer();
        this.refreshParentEstimateDetail();
      });
  };

  refreshParentEstimateDetail = () => {
    const { tab, openTabs, dispatch } = this.props;
    const payEstimateId = tab?.payEstimateId;
    if (!payEstimateId || !openTabs) return;
    const peTab = openTabs.find(
      (t) => t.docType === DOCTYPE.PROJECT_PAY_ESTIMATE_DETAIL && t.docId === payEstimateId
    );
    if (peTab) {
      dispatch(payEstimateActions.fetchDetail(payEstimateId, peTab.tabId));
    }
  };

  componentDidMount() {
    const { tab, dispatch } = this.props;
    dispatch(qSheetActions.fetchQSheet(tab.docId, tab.tabId));
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.qSheet;
    const curr = this.props.qSheet;
    if (curr && curr !== prev) {
      this.setState({
        cem6004Page: curr.cem6004PageNumber ?? "",
        cem6004Line: curr.cem6004LineNumber ?? "",
        cem6004Date: curr.cem6004PostingDate
          ? curr.cem6004PostingDate.substring(0, 10)
          : "",
        isSegregated: curr.isSegregated ?? false,
      });
    }
  }

  componentWillUnmount() {
    const { tab, dispatch } = this.props;
    dispatch({ type: "QS.CLOSE_TAB", tabId: tab.tabId });
  }

  render() {
    const {
      isReadOnly, tab, qSheet,
      userId, dispatch,
      isRE, isRE_A, isSR, userRoleCodes,
    } = this.props;
    const { liveData, refreshing, cem6004Page, cem6004Line, cem6004Date, isSegregated } = this.state;
    const calculatedBy = qSheet?.calculatedByFullName ?? null;
    const checkedBy    = qSheet?.checkedByFullName ?? null;
    const checkedOn    = qSheet?.checkedOn
      ? new Date(qSheet.checkedOn).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" })
      : null;

    const status        = qSheet?.statusLabel ?? tab?.status ?? "Draft";
    const isDraft       = status === "Draft";
    const isCalculated  = status === "Calculated";
    const isChecked     = status === "Checked";
    const badge         = STATUS_BADGE[status] ?? STATUS_BADGE.Draft;

    // Quantities locked once Checked (all roles)
    const quantitiesReadOnly = isReadOnly || isChecked;
    // Adjustments locked once Checked unless RE
    const adjustmentReadOnly = isReadOnly || (isChecked && !isRE && !isRE_A);

    const fmtDate   = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { year: "2-digit", month: "2-digit", day: "2-digit" }) : "";
    const startDate = fmtDate(tab?.startDate);
    const endDate   = fmtDate(tab?.endDate);

    return (
      <React.Fragment>

        {/* ── QSheet Header (bid item info + ItemEstimateDetails) ──── */}
        <QHeader
          qSheet={qSheet} tab={tab} jobStamp={this.props.jobStamp}
          calculatedBy={calculatedBy} checkedBy={checkedBy} checkedOn={checkedOn}
          badge={badge} startDate={startDate} endDate={endDate}
          estimateNumber={tab?.estimateNumber ?? "?"}
          isSegregated={isSegregated}
          isDraft={isDraft}
          onToggleSegregation={!isReadOnly ? () => this.setState(prev => ({ isSegregated: !prev.isSegregated })) : undefined}
        />

        {/* ── Main body: quantities/adjustments + totals sidebar ───── */}
        <div className="d-flex align-items-start">
          <div className="flex-grow-1 min-width-0">
            <QBody
              ref={this.bodyRef}
              qSheetId={qSheet?.qSheetId ?? null}
              projectId={tab?.projectId}
              rows={qSheet?.rows ?? []}
              adjustments={qSheet?.adjustments ?? []}
              attachments={qSheet?.attachments ?? []}
              unitOfMeasure={qSheet?.unitOfMeasure}
              ccoQuantity={qSheet?.ccoQuantity ?? 0}
              bidItemCcos={qSheet?.bidItemCcos ?? []}
              submittedAdrCount={qSheet?.submittedAdrCount ?? 0}
              notesRemarks={qSheet?.notesRemarks ?? ""}
              payQuantityIncludesCcoWork={qSheet?.payQuantityIncludesCcoWork ?? false}
              quantitiesReadOnly={quantitiesReadOnly}
              adjustmentReadOnly={adjustmentReadOnly}
              onDataChange={this.handleDataChange}
              onUploadAttachment={this.handleUploadAttachment}
              onUploadsDone={this.handleUploadsDone}
              onDeleteAttachment={this.handleDeleteAttachment}
            />
          </div>
          <div className="Q-totals-sidebar">
            <QTotals qSheet={qSheet} liveData={liveData}
              isDraft={isDraft} refreshing={refreshing}
              onRefreshQuantities={this.handleRefreshQuantities}
            />
          </div>
        </div>

        {/* ── CEM-6004 Cross-Reference (RE only, post-Draft) ───────── */}
        {/* {(isRE || isRE_A) && !isDraft && (
          <div className="card mb-2 mx-2">
            <div className="card-header py-1">
              <b>CEM-6004 Cross-Reference</b>
              {qSheet?.cem6004RecordedByFullName && (
                <span className="ml-3 text-muted small">
                  Recorded by {qSheet.cem6004RecordedByFullName}
                  {qSheet.cem6004RecordedOn
                    ? ` on ${new Date(qSheet.cem6004RecordedOn).toLocaleDateString("en-US")}`
                    : ""}
                </span>
              )}
            </div>
            <div className="card-body py-2">
              <div className="form-row align-items-center">
                <label className="col-auto col-form-label col-form-label-sm pr-1"><b>Page #:</b></label>
                <div className="col-1">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={cem6004Page}
                    disabled={isReadOnly}
                    onChange={e => this.setState({ cem6004Page: e.target.value })}
                  />
                </div>
                <label className="col-auto col-form-label col-form-label-sm pr-1 pl-3"><b>Line #:</b></label>
                <div className="col-1">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={cem6004Line}
                    disabled={isReadOnly}
                    onChange={e => this.setState({ cem6004Line: e.target.value })}
                  />
                </div>
                <label className="col-auto col-form-label col-form-label-sm pr-1 pl-3"><b>Posting Date:</b></label>
                <div className="col-2">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={cem6004Date}
                    disabled={isReadOnly}
                    onChange={e => this.setState({ cem6004Date: e.target.value })}
                  />
                </div>
                {!isReadOnly && (
                  <div className="col-auto pl-3">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      type="button"
                      disabled={!cem6004Page || !cem6004Line || !cem6004Date}
                      onClick={this.handleSaveCem6004Reference}
                    >
                      Save Reference
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )} */}

        {/* ── Action bar ───────────────────────────────────────────── */}
        <div className="card mb-2 mx-2 bg-lightgray">
          <div className="card-body">
            <div className="d-flex flex-row-reverse">
              <div>

                {/* Draft → Save Draft + Mark Calculated */}
                {isDraft && (
                  <>
                    <button
                      className="btn btn-primary mr-3"
                      type="button"
                      onClick={() => {
                        this.handleSave().then(() =>
                          dispatch(qSheetActions.calculateQSheet(tab.docId, tab.tabId))
                            .then(() => dispatch(qSheetActions.fetchQSheet(tab.docId, tab.tabId)))
                            .then(() => this.refreshParentEstimateDetail())
                        );
                      }}
                    >
                      <b>Mark Calculated</b>
                    </button>
                    <span className="mr-3 submit-separator">|</span>
                    <button
                      className="btn btn-secondary mr-2"
                      type="button"
                      onClick={this.handleSave}
                    >
                      Save Draft
                    </button>
                  </>
                )}

                {/* Calculated → Mark Checked (disabled if calculator = current user and not RE/SR) */}
                {isCalculated && (
                  <>
                    <button
                      className="btn btn-success mr-3"
                      type="button"
                      disabled={
                        qSheet?.calculatedByUserId === userId &&
                        !isRE && !isRE_A && !isSR
                      }
                      title={
                        qSheet?.calculatedByUserId === userId && !isRE && !isRE_A && !isSR
                          ? "A QSheet cannot be Checked by its own Calculator unless they hold RE or SR role."
                          : undefined
                      }
                      onClick={() => {
                        dispatch(qSheetActions.checkQSheet(tab.docId, userRoleCodes, tab.tabId))
                          .then(() => dispatch(qSheetActions.fetchQSheet(tab.docId, tab.tabId)))
                          .then(() => this.refreshParentEstimateDetail());
                      }}
                    >
                      <b>Mark Checked</b>
                    </button>
                    {(isRE || isRE_A) && !isReadOnly && (
                      <>
                        <span className="mr-3 submit-separator">|</span>
                        <button
                          className="btn btn-outline-secondary mr-2"
                          type="button"
                          onClick={() =>
                            dispatch(qSheetActions.revertQSheet(tab.docId, "Draft", tab.tabId))
                              .then(() => dispatch(qSheetActions.fetchQSheet(tab.docId, tab.tabId)))
                              .then(() => this.refreshParentEstimateDetail())
                          }
                        >
                          Revert to Draft
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* Checked → RE can revert to Calculated (not allowed on closed estimates) */}
                {isChecked && (isRE || isRE_A) && !isReadOnly && (
                  <button
                    className="btn btn-outline-secondary mr-2"
                    type="button"
                    onClick={() =>
                      dispatch(qSheetActions.revertQSheet(tab.docId, "Calculated", tab.tabId))
                        .then(() => dispatch(qSheetActions.fetchQSheet(tab.docId, tab.tabId)))
                        .then(() => this.refreshParentEstimateDetail())
                    }
                  >
                    Revert to Calculated
                  </button>
                )}

                {/* Print — always available */}
                <button
                  className="btn btn-outline-secondary mr-2"
                  type="button"
                  onClick={this.handlePrint}
                >
                  Print
                </button>

              </div>
            </div>
          </div>
        </div>

      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { tab } = ownProps;
  const user = state.authentication.user;
  const projectRole = user?.projectRoles?.find((pr) => pr.projectId === tab.projectId);
  const roleClass = projectRole?.projectRoleClass;

  // Check if the parent estimate is closed — either from the tab itself (set at open time)
  // or from any open estimate detail in the store (reflects live close events).
  const estimateDetail = Object.values(state.payEstimate.openEstimateDetails ?? {})
    .find((d) => d.payEstimateId === tab.payEstimateId);
  const isReadOnly = estimateDetail?.isClosed ?? tab?.isClosed ?? false;

  const isRE   = roleClass === "RESR";
  const isRE_A = roleClass === "RESRA";
  const isSR   = roleClass === "CEBCERESR" || roleClass === "CEBCE";
  const userRoleCodes = [
    isRE   && "RE",
    isRE_A && "RE",
    isSR   && "SR",
  ].filter(Boolean);

  return {
    qSheet: state.payEstimate.openQSheets[tab.tabId] || null,
    jobStamp: state.projects.jobStamps[tab.projectId] || null,
    userId: user?.userId,
    userRoleCodes,
    isReadOnly,
    isInspector: roleClass === "ARESR",
    isRE,
    isRE_A,
    isSR,
    isOE: roleClass === "OE" || roleClass === "OA",
    updating: state.payEstimate.updating,
    openTabs: state.mainTabs.openTabs,
  };
};

export default connect(mapStateToProps)(QSheet);
